export type CardStatus =
  | 'VALID_CARD'
  | 'CARD_DECLINED'
  | 'NO_CARD'
  | 'CONSENT_PENDING'
  | 'EXPIRED_CARD';
export type PaymentPlan =
  | 'ACTIVE'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'FAILED';

export type TransactionType = 'CHARGE' | 'REFUND' | 'PRE_AUTH' | 'CAPTURE';

export type TransactionStatus =
  | 'SUCCESS'
  | 'FAILED'
  | 'PENDING'
  | 'REFUNDED'
  | 'CANCELLED';

export interface Transaction {
  id: string;
  clientId: string;
  clientName: string;
  stripePaymentIntentId: string;
  transactionType: TransactionType;
  amount: number;
  currency: string;
  status: TransactionStatus;
  description: string;
  refundedAmount: number;
  isReversible: boolean;
  createdAt: string; // ISO Date string
  reversalReason: string;
  providerId: string;
  providerName: string;
  metadata: {
    disputed_reason: string;
  };
}

export interface TransactionPagination {
  page: number;
  offset: number;
  limit: number;
  total_elements: number;
  total_pages: number;
  total: number;
}

export interface TransactionResponse {
  transactions: Transaction[];
  pagination: TransactionPagination;
  totalCount: number;
}

export interface PatientRow {
  client_id: string;
  patient_name: string;
  patient_balance: number;
  payment_plan: PaymentPlan;
  card_status: CardStatus;
  last_charge_at: string; // ISO Date string
  next_appointment_at: string; // ISO Date string
}

export interface DirectoryRow {
  provider_id: string;
  provider_name: string;
  provider_balance: number;
  payment_plan: PaymentPlan;
  card_status: CardStatus;
  last_charge_at: string;
}

export interface TableData {
  key: string;
  label: string;
  sortable: boolean;
  orderBy?: string;
}

export interface Pagination {
  total: number;
  page: number;
  offset: number;
  limit: number;
}

export interface PatientResponse {
  rows: PatientRow[];
  pagination: Pagination;
}

export interface DirectoryPatientResponse {
  rows: DirectoryRow[];
  pagination: Pagination;
}

export enum SortingKey {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum BillingDashboardTabName {
  PATIENTS = 'patients',
  TRANSACTIONS = 'transactions',
  REPORTS = 'reports',
}

export enum dataKey {
  PROVIDER_NAME = 'provider_name',
  PROVIDER_BALANCE = 'provider_balance',
  PATIENT_NAME = 'patient_name',
  PATIENT_BALANCE = 'patient_balance',
  PAYMENT_PLAN = 'payment_plan',
  CARD_STATUS = 'card_status',
  LAST_CHARGE_AT = 'last_charge_at',
  NEXT_APPOINTMENT_AT = 'next_appointment_at',
  ACTIONS = 'actions',
  TRANSACTION_ID = 'id',
  TRANSACTION_PATIENT_NAME = 'patient_name',
  TRANSACTION_AMOUNT = 'amount',
  TRANSACTION_DATE = 'createdAt',
  TRANSACTION_STATUS = 'status',
  TRANSACTION_ACTIONS = 'actions',
  TRANSACTION_NOTE = 'note',
  TRANSACTION_PROVIDER_NAME = 'provider_name',
}

export enum filterKey {
  I2I_PATIENT = 'i2i_patient',
  ALL_PATIENT = 'all_patient',
  VALID_CARD = 'VALID_CARD',
  CARD_DECLINED = 'CARD_DECLINED',
  NO_CARD = 'NO_CARD',
  CONSENT_PENDING = 'CONSENT_PENDING',
  EXPIRED_CARD = 'EXPIRED_CARD',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  REFUNDED = 'REFUNDED',
  CANCELLED = 'CANCELLED',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
}

export enum filterOptionsText {
  all_i2i_managed = 'i2i Managed',
  all_patient = 'All Patients',
  filter_by_card_status = 'CARD STATUS',
  filter_by_show = 'SHOW',
  valid_card = 'Valid Card',
  card_declined = 'Card Declined',
  no_card = 'No Card',
  consent_pending = 'Consent Pending',
  expired_card = 'Expired Card',
}

export enum UserTypeBasedOnPlatform {
  PROVIDER = 'PROVIDER',
  CLIENT = 'CLIENT',
}

export const patientTableHeaderData = [
  { key: dataKey.PATIENT_NAME, label: 'Patient Name', sortable: true },
  { key: dataKey.PATIENT_BALANCE, label: 'Patient Balance', sortable: true },
  { key: dataKey.PAYMENT_PLAN, label: 'Payment Plan', sortable: false },
  { key: dataKey.CARD_STATUS, label: 'Card Status', sortable: true },
  { key: dataKey.LAST_CHARGE_AT, label: 'Balance Age', sortable: true },
  {
    key: dataKey.NEXT_APPOINTMENT_AT,
    label: 'Next Appointment',
    sortable: false,
  },
  { key: dataKey.ACTIONS, label: 'Actions', sortable: false },
];

export const directoryTableHeaderData = [
  { key: dataKey.PROVIDER_NAME, label: 'Provider Name', sortable: true },
  { key: dataKey.PROVIDER_BALANCE, label: 'Provider Balance', sortable: true },
  { key: dataKey.PAYMENT_PLAN, label: 'Payment Plan', sortable: false },
  { key: dataKey.CARD_STATUS, label: 'Card Status', sortable: true },
  { key: dataKey.LAST_CHARGE_AT, label: 'Balance Age', sortable: true },
  { key: dataKey.ACTIONS, label: 'Actions', sortable: false },
];

export const transactionTableHeaderData = [
  { key: dataKey.TRANSACTION_ID, label: 'ID', sortable: false },
  {
    key: dataKey.TRANSACTION_PATIENT_NAME,
    label: 'Patient Name',
    sortable: true,
  },
  { key: dataKey.TRANSACTION_AMOUNT, label: 'Amount', sortable: true },
  { key: dataKey.TRANSACTION_STATUS, label: 'Status', sortable: false },
  { key: dataKey.TRANSACTION_DATE, label: 'Date', sortable: false },
  {
    key: dataKey.TRANSACTION_ACTIONS,
    label: 'Actions',
    sortable: false,
  },
  // { key: dataKey.TRANSACTION_NOTE, label: 'Note', sortable: false },  // commented this as we need to enhance this  in future
];

export const directoryTransactionTableHeaderData = [
  { key: dataKey.TRANSACTION_ID, label: 'ID', sortable: false },
  {
    key: dataKey.TRANSACTION_PROVIDER_NAME,
    label: 'Provider Name',
    sortable: true,
  },
  { key: dataKey.TRANSACTION_AMOUNT, label: 'Amount', sortable: true },
  { key: dataKey.TRANSACTION_STATUS, label: 'Status', sortable: false },
  { key: dataKey.TRANSACTION_DATE, label: 'Date', sortable: false },
  {
    key: dataKey.TRANSACTION_PATIENT_NAME,
    label: 'Patient Onboarded',
    sortable: true,
  },
  {
    key: dataKey.TRANSACTION_ACTIONS,
    label: 'Actions',
    sortable: false,
  },
];

export const directoryPatientTransactionTableHeaderData = [
  { key: dataKey.TRANSACTION_ID, label: 'ID', sortable: false },
  { key: dataKey.TRANSACTION_AMOUNT, label: 'Amount', sortable: true },
  { key: dataKey.TRANSACTION_STATUS, label: 'Status', sortable: false },
  { key: dataKey.TRANSACTION_DATE, label: 'Date', sortable: false },
  {
    key: dataKey.TRANSACTION_PATIENT_NAME,
    label: 'Patient Onboarded',
    sortable: true,
  },
  {
    key: dataKey.TRANSACTION_ACTIONS,
    label: 'Actions',
    sortable: false,
  },
];

export const defaultBalanceAgeByAMD = '1900-01-01T00:00:00Z';

export const isDirectoryPlatformText = 'IS_DIRECTORY_PLATFORM';

export interface BillingHomepageSortDirective {
  field_name:
    | 'patient_balance'
    | 'card_status'
    | 'last_charge_at'
    | 'patient_name'
    | 'payment_plan'
    | 'provider_balance'
    | 'provider_name';
  sort_type: 'ASC' | 'DESC';
}

export interface BillingHomepageSorting {
  directives: BillingHomepageSortDirective[];
}

export interface BillingHomepagePagination {
  page: number;
  offset?: number;
  limit: number;
  total?: number;
  total_pages?: number;
}

export interface BillingHomepageRequest {
  client_ids?: string[];
  user_ids?: string[];
  i2i_managed_only: boolean;
  pagination: BillingHomepagePagination;
  card_status_filter?: CardStatus[];
  sorting: BillingHomepageSorting;
}

export interface DateRangeFilter {
  startDate?: string;
  endDate?: string;
}

export interface TransactionSortDirective {
  field_name: 'patient_name' | 'amount' | 'provider_name';
  sort_type: SortingKey;
}

export interface TransactionSorting {
  directives: TransactionSortDirective[];
}

export interface TransactionSearchPagination {
  page: number;
  offset?: number;
  limit: number;
  total?: number;
  total_pages?: number;
}

export interface TransactionSearchRequest {
  clientIds?: string[];
  providerIds?: string[];
  transactionTypes?: TransactionType[];
  statuses?: TransactionStatus[];
  dateRange?: DateRangeFilter;
  sorting: TransactionSorting;
  pagination: TransactionSearchPagination;
}
